import { cn } from '@/lib/utils';
import { ReactNode } from 'react';

interface CardProps {
    title?: string;
    /** Optional supporting line under the title (only when it adds info). */
    subtitle?: string;
    /** Right-aligned header controls (filters, buttons). */
    actions?: ReactNode;
    /** Optional footer region with a hairline top border. */
    footer?: ReactNode;
    children: ReactNode;
    className?: string;
    noPadding?: boolean;
}

export default function Card({
    title,
    subtitle,
    actions,
    footer,
    children,
    className,
    noPadding = false,
}: CardProps) {
    const hasHeader = Boolean(title || actions);
    return (
        <section
            className={cn(
                'septima-panel',
                'transition-[border-color,box-shadow,background-color] duration-card-hover',
                'hover:border-[var(--septima-border-strong)]',
                noPadding && 'overflow-hidden p-0',
                className,
            )}
        >
            {hasHeader && (
                <div
                    className={cn(
                        'septima-panel__header flex items-start justify-between gap-3',
                        noPadding && 'px-4 pt-4',
                    )}
                >
                    <div className="min-w-0">
                        {title && <h2>{title}</h2>}
                        {subtitle && (
                            <p className="text-annotation text-[rgb(var(--color-text-secondary))]">
                                {subtitle}
                            </p>
                        )}
                    </div>
                    {actions && (
                        <div className="flex flex-shrink-0 items-center gap-2">{actions}</div>
                    )}
                </div>
            )}
            {children}
            {footer && (
                <div
                    className={cn(
                        'mt-4 border-t border-[rgb(var(--color-divider))] pt-3',
                        noPadding && 'px-4 pb-4',
                    )}
                >
                    {footer}
                </div>
            )}
        </section>
    );
}
